import React, { FunctionComponent, Fragment } from 'react';
import { getReasonPhrase } from '@rbx/core';
import { Grid, Typography, makeStyles } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { TResponses } from '@rbx/docsite-utilities/dist/browser';
import ContentTable from '@modules/ui/shared/components/ContentTable';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import SchemaTable from './SchemaTable';
import ReturnTitle from './ReturnTitle';

const useStyles = makeStyles()(() => ({
  marginTop24: {
    marginTop: 24,
  },
  marginTop48: {
    marginTop: 48,
  },
  otherCodesLabel: {
    marginTop: 32,
    marginBottom: 16,
  },
  bold: {
    fontWeight: 500,
  },
}));

interface ResponseProps {
  responses: TResponses;
  resourceRefName: string;
  className?: string;
}

const Response: FunctionComponent<React.PropsWithChildren<ResponseProps>> = ({
  responses,
  resourceRefName,
  className,
}) => {
  const { translate } = useTranslation();
  const {
    classes: { marginTop24, marginTop48, bold, otherCodesLabel },
  } = useStyles();
  const successResponse = responses.success;
  const otherResponses = responses.others;
  if (!successResponse) {
    return null;
  }
  return (
    <Grid container direction='column' className={className}>
      <Typography variant='h4'>{translate('Label.Response')}</Typography>
      {!successResponse.isLongRunning ? (
        <Fragment>
          {resourceRefName === successResponse.refName ? (
            <ReturnTitle refName={resourceRefName} className={marginTop24} />
          ) : (
            <SchemaTable
              refName={successResponse.refName}
              className={marginTop24}
              showDescription
            />
          )}
        </Fragment>
      ) : (
        <Fragment>
          {/* The text here TBD */}
          <StandardMarkdownNoMargin className={marginTop24}>
            {translate('Description.AsyncResponse', {
              longRunningOperationsLink: '/cloud/reference/patterns#long-running-operations',
            })}
          </StandardMarkdownNoMargin>
          <Typography variant='h6' className={marginTop48}>
            {translate('Label.Response')}
          </Typography>
          {resourceRefName === successResponse.responseRefName ? (
            <ReturnTitle refName={resourceRefName} className={marginTop24} />
          ) : (
            <SchemaTable
              className={marginTop24}
              refName={successResponse.responseRefName}
              showDescription
              showNoneIfTableIsEmpty
            />
          )}
          <Typography variant='h6' className={marginTop24}>
            {translate('Label.Metadata')}
          </Typography>
          <SchemaTable
            className={marginTop24}
            refName={successResponse.metadataRefName}
            showDescription
            showNoneIfTableIsEmpty
          />
        </Fragment>
      )}
      {otherResponses.length > 0 && (
        <Fragment>
          <Typography variant='largeLabel1' className={otherCodesLabel}>
            {translate('Label.OtherCodes')}
          </Typography>
          <ContentTable
            data={otherResponses}
            renderer={(row: unknown) => {
              const { code, description } = row as { code: string; description: string };
              return {
                name: (
                  <Typography className={bold} variant='body2'>{`${code} - ${getReasonPhrase(
                    code,
                  )}`}</Typography>
                ),
                summary: description,
              };
            }}
          />
        </Fragment>
      )}
    </Grid>
  );
};

export default Response;
